import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Closes the public staging site between demos.
 *
 * The client shows this build to third parties and wants it reachable only
 * while a demo is running — opened from /site-admin beforehand, closed again
 * afterwards. The flag lives on the server (see the site-access module), so
 * closing it applies to every visitor at once and cannot be undone by
 * clearing browser storage.
 *
 * Only the STAGE build mounts this; every other build renders its children
 * untouched and never calls the endpoint.
 */
const IS_STAGE_BUILD = process.env.REACT_APP_MODULE === 'STAGE';

// The admin screen has to stay reachable while the site is closed, or there
// would be no way to reopen it.
const ALWAYS_ALLOWED_PATHS = ['/site-admin'];

function UnavailableScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a1628',
        color: '#e6edf3',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
          fontSize: '28px',
        }}
      >
        <i className="fa-solid fa-lock" />
      </div>
      <h1 style={{ fontSize: '22px', fontWeight: 600, margin: 0 }}>
        Site is currently unavailable
      </h1>
      <p style={{ opacity: 0.65, marginTop: '12px', maxWidth: '420px', lineHeight: 1.6 }}>
        This site is not open to visitors right now. Please check back later.
      </p>
    </div>
  );
}

function StageAccessGate({ children }) {
  // Non-STAGE builds skip every bit of this, including the request.
  const [enabled, setEnabled] = useState(IS_STAGE_BUILD ? null : true);

  useEffect(() => {
    if (!IS_STAGE_BUILD) return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await api.get('/site-access/status');
        if (!cancelled) setEnabled(res?.data?.data?.enabled !== false);
      } catch {
        // Fail open. A backend blip must not black out a demo that is
        // already in progress — the switch is a convenience, not a security
        // boundary for anything sensitive.
        if (!cancelled) setEnabled(true);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!IS_STAGE_BUILD) return children;

  // Nothing is rendered until the answer arrives, so a closed site never
  // flashes its content first.
  if (enabled === null) return null;

  if (!enabled && !ALWAYS_ALLOWED_PATHS.includes(window.location.pathname)) {
    return <UnavailableScreen />;
  }

  return children;
}

export default StageAccessGate;
